<template>
  <div>
    <b-dropdown class="dropdown-files" :disabled="disabled">
      <template #button-content>
        <i class="icofont-download"></i>
        Exportar
      </template>

      <slot name="items"></slot>

      <b-dropdown-item
        v-if="pdfEnabled"
        href="#"
        @click.prevent="openWarning('pdf')"
      >
        <i class="icofont-file-pdf"></i>
        {{ pdfLabel }}
      </b-dropdown-item>

      <b-dropdown-item
        v-if="xlsEnabled"
        href="#"
        @click.prevent="openWarning('xls')"
      >
        <i class="icofont-file-excel"></i>
        {{ xlsLabel }}
      </b-dropdown-item>
    </b-dropdown>

    <download-excel
      v-if="xlsEnabled"
      ref="downloadExcel"
      :data="data"
      :fields="fields"
      worksheet="My Worksheet"
      :name="`${name}.xls`"
      class="d-none"
    >
      <span>Download</span>
    </download-excel>

    <b-modal
      v-model="warningModal"
      title="Aviso de Acesso a Dados Individualizados"
      centered
      hide-footer
      @hidden="resetWarning"
    >
      <p>
        Esta lista nominal tem dados individualizados e identificação das
        pessoas. O acesso é restrito e permitido somente a usuários autenticados
        com login e senha. Em conformidade com a Lei Geral de Proteção de Dados
        Pessoais (LGPD - Lei nº 13.709/2018), é expressamente proibido utilizar
        esses dados para finalidades distintas das previstas na LGPD ou
        divulgá-los sem a devida autorização das pessoas titulares desses dados.
      </p>

      <p>O uso indevido acarretará as penalidades previstas em lei.</p>

      <p>
        Ao prosseguir, você declara estar ciente e de acordo com estes termos.
      </p>

      <p>
        <strong>Atenção:</strong>
        Todas as solicitações de visualização de listas nominais das pessoas são
        registradas para futuras auditorias no sistema.
      </p>

      <b-form-checkbox v-model="accepted"> Li e concordo </b-form-checkbox>

      <div class="d-flex justify-content-end mt-4 warning-modal-actions">
        <b-button variant="outline-primary" @click="warningModal = false">
          Cancelar
        </b-button>

        <b-button
          variant="primary"
          :disabled="!accepted"
          @click="confirmDownload"
        >
          Avançar
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'DownloadFiles',

  props: {
    data: {
      type: [Array, Boolean],
      default: () => [],
    },

    fields: {
      type: Object,
      default: () => ({}),
    },

    name: {
      type: String,
      default: 'file',
    },

    callbackPdf: {
      type: [Function, Boolean],
      default: false,
    },

    pdfEnabled: {
      type: Boolean,
      default: true,
    },

    xlsEnabled: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    pdfLabel: {
      type: String,
      default: 'PDF',
    },

    xlsLabel: {
      type: String,
      default: 'XLS',
    },

    logEnabled: {
      type: Boolean,
      default: true,
    },

    warningEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      warningModal: false,
      accepted: false,
      downloadType: null,
      downloadCallback: null,
    }
  },

  methods: {
    /**
     * Abre o aviso antes de realizar o download.
     *
     * Pode receber um tipo de download interno ou um callback
     * para downloads personalizados adicionados pelo slot #items.
     *
     * @param {string|null} type
     * @param {Function|null} callback
     * @returns {void}
     */
    openWarning(type = null, callback = null) {
      if (!this.warningEnabled) {
        if (typeof callback === 'function') {
          callback()
          return
        }

        if (type === 'pdf') {
          this.generatePdf()
          return
        }

        if (type === 'xls') {
          this.generateXls()
        }

        return
      }

      this.downloadType = type
      this.downloadCallback = callback
      this.accepted = false
      this.warningModal = true
    },

    /**
     * Confirma o aceite dos termos e inicia o download.
     *
     * @returns {void}
     */
    confirmDownload() {
      if (!this.accepted) {
        return
      }

      const callback = this.downloadCallback
      const type = this.downloadType

      this.warningModal = false

      if (typeof callback === 'function') {
        callback()
        return
      }

      if (type === 'pdf') {
        this.generatePdf()
        return
      }

      if (type === 'xls') {
        this.generateXls()
      }
    },

    /**
     * Gera o PDF e registra o download em log.
     *
     * @returns {void}
     */
    generatePdf() {
      if (this.logEnabled) {
        this.postLog()
      }

      if (typeof this.callbackPdf === 'function') {
        this.callbackPdf()
      }
    },

    /**
     * Gera o XLS e registra o download em log.
     *
     * @returns {void}
     */
    generateXls() {
      if (this.logEnabled) {
        this.postLog()
      }

      this.$nextTick(() => {
        const component = this.$refs.downloadExcel

        if (component && component.generate) {
          component.generate()
        }
      })
    },

    /**
     * Limpa os dados do aviso.
     *
     * @returns {void}
     */
    resetWarning() {
      this.accepted = false
      this.downloadType = null
      this.downloadCallback = null
    },

    /**
     * Registra o download na API para fins de auditoria.
     *
     * @returns {void}
     */
    postLog() {
      if (!this.logEnabled) {
        return
      }

      const payload = {
        id: this.$store.state.id,
        nome: this.$store.state.username,
        cod_ibge: this.$store.state.cod_ibge,
        page: this.$route.path,
      }

      this.$api.post('/app/logs/download', payload)
    },
  },
}
</script>

<style>
.dropdown-files button.dropdown-toggle {
  color: #fff;
  background-color: #545b62;
  border-color: #4e555b;
}

.dropdown-files ul {
  padding: 10px !important;
  border-radius: initial !important;
  align-items: center;
  justify-content: flex-start;
}

.dropdown-files ul li a {
  padding: 5px 15px;
}

.dropdown-files .btn-exportar {
  padding: 5px 15px;
}

.warning-modal-actions {
  gap: 10px;
}
</style>
